import { useState, useEffect } from "react";
import {
  Plus,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  X,
  Save,
  Bug,
  Slash,
  Zap,
  ScanLine,
  CircleOff,
  ExternalLink,
  MapPin,
} from "lucide-react";
import axios from "axios";
import { API_URL } from "../utils/api-config";
import Button from "./ui/Button";
import { Card } from "./ui/Card";
import Chip from "./ui/Chip";
import Spinner from "./ui/Spinner";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "./ui/Dialog";
import Input from "./ui/Input";
import Select from "./ui/Select";
import IconButton from "./ui/IconButton";
import VehicleDamageReporter from "./VehicleDamageReporter";

interface Damage {
  _id?: string;
  type: "Rayure" | "Choc" | "Manquant" | "Casse";
  face: "avant" | "arriere" | "coteConducteur" | "cotePassager" | "toit";
  description: string;
  date: string;
  vehiclePlate?: string;
  assignmentId?: string;
  images?: string[];
  cost?: number;
  position?: { x: number; y: number };
  isRepaired?: boolean;
  repairedAt?: string;
  driverName?: string;
  driverId?: string;
}

interface DamagesSectionProps {
  driverId: string;
  onUpdate?: () => void;
}

const getDamageIcon = (type: string) => {
  switch (type) {
    case "Rayure":
      return <ScanLine className="w-5 h-5" />;
    case "Choc":
      return <Zap className="w-5 h-5" />;
    case "Manquant":
      return <CircleOff className="w-5 h-5" />;
    case "Casse":
      return <Slash className="w-5 h-5" />;
    default:
      return <Bug className="w-5 h-5" />;
  }
};

const getFaceLabel = (face: string) => {
  const labels: { [key: string]: string } = {
    avant: "Avant",
    arriere: "Arrière",
    coteConducteur: "Côté conducteur",
    cotePassager: "Côté passager",
    toit: "Toit",
  };
  return labels[face] || face;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR");
};

const DamagesSection = ({ driverId, onUpdate }: DamagesSectionProps) => {
  const [damages, setDamages] = useState<Damage[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedDamage, setExpandedDamage] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    type: "Rayure" as Damage["type"],
    face: "avant" as Damage["face"],
    description: "",
    vehiclePlate: "",
    images: [] as string[],
    cost: 0,
  });
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [showVisualReporter, setShowVisualReporter] = useState(false);
  const [periodFilter, setPeriodFilter] = useState<'all' | 'week' | 'month' | 'quarter' | 'year'>('all');

  // Fonction de filtrage par période
  const filterByPeriod = (dateString: string | undefined) => {
    if (periodFilter === 'all' || !dateString) return true;
    const itemDate = new Date(dateString);
    const now = new Date();
    switch (periodFilter) {
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return itemDate >= weekAgo;
      case 'month':
        const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        return itemDate >= monthAgo;
      case 'quarter':
        const quarterAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        return itemDate >= quarterAgo;
      case 'year':
        const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        return itemDate >= yearAgo;
      default:
        return true;
    }
  };

  // Dégâts filtrés
  const filteredDamages = damages.filter(d => filterByPeriod(d.date));

  const fetchDamages = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/drivers/${driverId}/damages`
      );
      setDamages(data);
    } catch (error) {
      console.error("Erreur lors du chargement des dégâts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadDamages = async () => {
      if (driverId) {
        setLoading(true);
        try {
          const { data } = await axios.get(
            `${API_URL}/drivers/${driverId}/damages`
          );
          setDamages(data);
        } catch (error) {
          console.error("Erreur lors du chargement des dégâts:", error);
        } finally {
          setLoading(false);
        }
      }
    };
    loadDamages();
  }, [driverId]);

  const handleOpenForm = () => {
    setFormData({
      type: "Rayure",
      face: "avant",
      description: "",
      vehiclePlate: "",
      images: [],
    });
    setPreviewImages([]);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
  };

  const handleFormChange = (
    e: React.ChangeEvent<
      HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploading(true);

    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setPreviewImages([...previewImages, ...newPreviews]);

    setTimeout(() => {
      setFormData({
        ...formData,
        images: [...formData.images, ...newPreviews],
      });
      setUploading(false);
    }, 1000);
  };

  const removePreviewImage = (index: number) => {
    const newPreviews = [...previewImages];
    newPreviews.splice(index, 1);
    setPreviewImages(newPreviews);

    const newImages = [...formData.images];
    newImages.splice(index, 1);
    setFormData({ ...formData, images: newImages });
  };

  const handleSubmit = async () => {
    try {
      await axios.post(`${API_URL}/drivers/${driverId}/damages`, formData);
      alert("Dégât enregistré avec succès !");
      handleCloseForm();
      fetchDamages();
      if (onUpdate) onUpdate();
    } catch (error) {
      alert("Erreur lors de l'enregistrement du dégât");
      console.error(error);
    }
  };

  const handleOpenImage = (img: string) => {
    setCurrentImage(img);
    setImageDialogOpen(true);
  };

  const handleAddDamageVisual = async (damage: Damage) => {
    try {
      await axios.post(`${API_URL}/drivers/${driverId}/damages`, {
        type: damage.type,
        face: damage.face,
        description: damage.description,
        cost: damage.cost,
        images: damage.images || [],
        position: damage.position
      });
      alert("Dégât enregistré avec succès !");
      fetchDamages();
      if (onUpdate) onUpdate();
    } catch (error) {
      alert("Erreur lors de l'enregistrement du dégât");
      console.error(error);
    }
  };

  const handleUpdateDamageVisual = async (damageId: string, updates: Partial<Damage>) => {
    try {
      await axios.put(`${API_URL}/drivers/${driverId}/damages/${damageId}`, updates);
      alert("Dégât mis à jour avec succès !");
      fetchDamages();
      if (onUpdate) onUpdate();
    } catch (error) {
      alert("Erreur lors de la mise à jour du dégât");
      console.error(error);
    }
  };

  const handleDeleteDamageVisual = async (damageId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce dégât ?")) return;
    
    try {
      await axios.delete(`${API_URL}/drivers/${driverId}/damages/${damageId}`);
      alert("Dégât supprimé avec succès !");
      fetchDamages();
      if (onUpdate) onUpdate();
    } catch (error) {
      alert("Erreur lors de la suppression du dégât");
      console.error(error);
    }
  };

  return (
    <>
      {/* En-tête de section */}
      <div className="flex flex-col gap-3 mt-8 mb-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Bug className="w-6 h-6 text-error" />
            Sinistres générés ({filteredDamages.length}/{damages.length})
          </h2>
          <div className="flex gap-2">
            <Button
              variant={showVisualReporter ? "contained" : "outlined"}
              color={showVisualReporter ? "primary" : "error"}
              size="small"
              startIcon={<MapPin className="w-4 h-4" />}
              onClick={() => setShowVisualReporter(!showVisualReporter)}
            >
              {showVisualReporter ? "Masquer" : "Mode visuel"}
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<Plus className="w-4 h-4" />}
              onClick={handleOpenForm}
            >
              Formulaire rapide
            </Button>
          </div>
        </div>
        {/* Filtre de périodicité */}
        {damages.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Période:</span>
            <select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value as typeof periodFilter)}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-error/50"
            >
              <option value="all">Toute période</option>
              <option value="week">7 derniers jours</option>
              <option value="month">Ce mois</option>
              <option value="quarter">3 derniers mois</option>
              <option value="year">Cette année</option>
            </select>
          </div>
        )}
      </div>

      {/* Composant visuel de signalement */}
      {showVisualReporter && (
        <div className="mb-8">
          <VehicleDamageReporter
            damages={damages}
            onAddDamage={handleAddDamageVisual}
            onUpdateDamage={handleUpdateDamageVisual}
            onDeleteDamage={handleDeleteDamageVisual}
          />
        </div>
      )}

      {/* Liste des sinistres */}
      {loading ? (
        <div className="flex justify-center py-6">
          <Spinner size="medium" />
        </div>
      ) : damages.length === 0 ? (
        <div className="py-6 px-4 text-center">
          <p className="text-gray-500">
            Aucun sinistre généré enregistré pour ce chauffeur.
          </p>
        </div>
      ) : filteredDamages.length === 0 ? (
        <div className="py-6 px-4 text-center">
          <p className="text-gray-500">
            Aucun sinistre pour cette période.
          </p>
        </div>
      ) : (
        <div className="mb-8 space-y-3">
          {filteredDamages.map((damage, index) => (
            <Card
              key={damage._id || index}
              className="overflow-hidden border-l-4 border-error"
            >
              <div
                onClick={() =>
                  setExpandedDamage(
                    expandedDamage === damage._id ? null : damage._id || null
                  )
                }
                className="cursor-pointer p-4 bg-red-50 flex justify-between items-center hover:bg-red-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-error text-white flex items-center justify-center">
                    {getDamageIcon(damage.type)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {damage.type} - {getFaceLabel(damage.face)}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Véhicule: {damage.vehiclePlate || "N/A"} -{" "}
                      {damage.date
                        ? formatDate(damage.date)
                        : "Date non spécifiée"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Chip size="small" label={damage.type} color="error" />
                  {expandedDamage === damage._id ? (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  )}
                </div>
              </div>

              {expandedDamage === damage._id && (
                <div className="p-4 bg-white border-t border-gray-200">
                  <p className="text-sm mb-2">
                    <strong>Description:</strong> {damage.description}
                  </p>

                  {damage.assignmentId && damage.vehiclePlate && (
                    <div className="flex items-center justify-between mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                      <div>
                        <p className="text-xs text-gray-600">Affectation ID:</p>
                        <p className="text-sm font-mono font-medium text-gray-900">
                          {damage.assignmentId}
                        </p>
                      </div>
                      <Button
                        to={`/vehicules/historique/${damage.vehiclePlate}/processus/${damage.assignmentId}`}
                        variant="outlined"
                        size="small"
                        startIcon={<ExternalLink className="w-3 h-3" />}
                      >
                        Voir le processus
                      </Button>
                    </div>
                  )}

                  {Array.isArray(damage.images) && damage.images.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium mb-2">Photos:</p>
                      <div className="flex flex-wrap gap-2">
                        {damage.images.map((img, idx) => (
                          <div
                            key={idx}
                            className="w-20 h-20 rounded border border-gray-300 overflow-hidden cursor-pointer hover:opacity-75 transition-opacity"
                            onClick={() => handleOpenImage(img)}
                          >
                            <img
                              src={img}
                              alt={`Dégât ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Formulaire */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle onClose={handleCloseForm}>Signaler un dégât</DialogTitle>
        <DialogContent>
          <div className="space-y-4">
            <Select
              fullWidth
              label="Type de dégât"
              name="type"
              value={formData.type}
              onChange={handleFormChange}
            >
              <option value="Rayure">Rayure</option>
              <option value="Choc">Choc</option>
              <option value="Manquant">Manquant</option>
              <option value="Casse">Casse</option>
            </Select>

            <Select
              fullWidth
              label="Face du véhicule"
              name="face"
              value={formData.face}
              onChange={handleFormChange}
            >
              <option value="avant">Avant</option>
              <option value="arriere">Arrière</option>
              <option value="coteConducteur">Côté conducteur</option>
              <option value="cotePassager">Côté passager</option>
              <option value="toit">Toit</option>
            </Select>

            <Input
              fullWidth
              label="Plaque du véhicule"
              name="vehiclePlate"
              value={formData.vehiclePlate}
              onChange={handleFormChange}
            />

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <Input
              fullWidth
              label="Coût estimé (€)"
              name="cost"
              type="number"
              value={formData.cost}
              onChange={handleFormChange}
              min={0}
              step={0.01}
              placeholder="0.00"
            />

            {/* Upload d'images */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Photos</p>
              <input
                accept="image/*"
                id="damage-image-upload"
                type="file"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
              <label htmlFor="damage-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImageIcon className="w-4 h-4" />}
                  disabled={uploading}
                >
                  {uploading ? "Téléchargement..." : "Ajouter des photos"}
                </Button>
              </label>

              {Array.isArray(previewImages) && previewImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {previewImages.map((preview, idx) => (
                    <div
                      key={idx}
                      className="relative w-20 h-20 rounded border border-gray-300 overflow-hidden"
                    >
                      <img
                        src={preview}
                        alt={`Dégât ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <IconButton
                        size="small"
                        className="absolute top-0 right-0 bg-white bg-opacity-70 hover:bg-opacity-90"
                        onClick={() => removePreviewImage(idx)}
                      >
                        <X className="w-3 h-3" />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} variant="text">
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="error"
            startIcon={<Save className="w-4 h-4" />}
            disabled={uploading}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Image */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
      >
        <DialogTitle onClose={() => setImageDialogOpen(false)}>
          Image du dégât
        </DialogTitle>
        <DialogContent>
          {currentImage && (
            <img src={currentImage} alt="Dégât" className="w-full h-auto" />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)} variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DamagesSection;
